import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { OptimizedEarthGlobe } from './OptimizedEarthGlobe';
import { BOOKING_LINK, CANDIDATE_APPLY_ROUTE } from '../constants';

const Hero: React.FC = () => {
    const navigate = useNavigate();

    const handleStartHiring = () => {
        window.open(BOOKING_LINK, '_blank', 'noopener,noreferrer');
    };

    const handleExploreOpportunities = () => {
        navigate(CANDIDATE_APPLY_ROUTE);
    };

    return (
        <section className="relative text-center px-6 pt-12 pb-4 sm:py-12"  >
            <div className="container mx-auto relative max-w-[1600px]">
                {/* Hero Title - Figma Mobile Spec */}
                <motion.h1
                    className="text-white mb-6 w-[316px] sm:w-full sm:!text-[48px] sm:!leading-[66px] lg:!text-[128px] lg:!leading-[175px] mx-auto"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                    style={{
                        fontFamily: 'Manrope',
                        fontWeight: 500,
                        fontSize: '32px',
                        lineHeight: '44px',
                        letterSpacing: '-0.05em',
                        textAlign: 'center'
                    }}
                >
                    Recruiting. Reinvented.
                </motion.h1>

                {/* Subtitle - Figma Mobile Spec */}
                <motion.p
                    className="text-white mx-auto mb-8 w-[342px] sm:w-full sm:mb-12 sm:!text-[28px] sm:!leading-[38px] lg:!text-[45px] lg:!leading-[61px] sm:!max-w-[900px] lg:!max-w-[1460px]"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
                    style={{
                        fontFamily: 'Manrope',
                        fontWeight: 200,
                        fontSize: '16px',
                        lineHeight: '22px',
                        letterSpacing: '-0.05em',
                        textAlign: 'center'
                    }}
                >
                    AI-powered recruiting that connects companies with top passive talent
                </motion.p>

                {/* CTA Buttons - Figma Mobile Spec */}
                <motion.div
                    className="flex flex-row justify-center items-center gap-[34px] sm:gap-6 mb-8 sm:mb-32 w-[298px] sm:w-auto mx-auto"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
                >
                    {/* Find Talent Button - Figma Spec */}
                    <motion.button
                        className="flex items-center justify-center transition-all sm:!w-[280px] lg:!w-[374px] sm:!h-[55px] lg:!h-[77px] sm:rounded-[8px] lg:!rounded-[10px] sm:gap-2 sm:!px-[30px]"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={handleStartHiring}
                        style={{
                            width: '126px',
                            height: '33px',
                            padding: '12px 30px',
                            gap: '4px',
                            background: 'linear-gradient(180deg, rgba(61, 66, 68, 0) -40.91%, rgba(158, 170, 176, 0.248) 132.95%)',
                            boxShadow: 'inset 0px 8px 10px rgba(246, 249, 255, 0.08)',
                            filter: 'drop-shadow(0px 6px 40px rgba(255, 255, 255, 0.08))',
                            backdropFilter: 'blur(50px)',
                            borderRadius: '5px',
                            border: 'none',
                            cursor: 'pointer'
                        }}
                    >
                        <span
                            className="sm:!text-[18px] lg:!text-[24px]"
                            style={{
                                fontFamily: 'Inter',
                                fontWeight: 500,
                                fontSize: '12px',
                                lineHeight: '20px',
                                color: '#FFFFFF',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            Start Hiring
                        </span>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="sm:!w-[18px] sm:!h-[18px] lg:!w-[24px] lg:!h-[24px]">
                            <path d="M9 18L15 12L9 6" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </motion.button>

                    {/* Explore Opportunities Button - Figma Spec */}
                    <motion.button
                        className="flex items-center justify-center transition-all sm:!w-[280px] lg:!w-[374px] sm:!h-[55px] lg:!h-[77px] sm:rounded-[8px] lg:!rounded-[10px] sm:gap-2 sm:!px-[30px]"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={handleExploreOpportunities}
                        style={{
                            width: '160px',
                            height: '33px',
                            padding: '12px 30px',
                            gap: '4px',
                            background: 'linear-gradient(180deg, rgba(61, 66, 68, 0) -40.91%, rgba(158, 170, 176, 0.248) 132.95%)',
                            boxShadow: 'inset 0px 8px 10px rgba(246, 249, 255, 0.08)',
                            filter: 'drop-shadow(0px 6px 40px rgba(255, 255, 255, 0.08))',
                            backdropFilter: 'blur(50px)',
                            borderRadius: '5px',
                            border: 'none',
                            cursor: 'pointer'
                        }}
                    >
                        <span
                            className="sm:!text-[18px] lg:!text-[24px]"
                            style={{
                                fontFamily: 'Inter',
                                fontWeight: 500,
                                fontSize: '12px',
                                lineHeight: '20px',
                                color: '#FFFFFF',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            Explore Opportunities
                        </span>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="sm:!w-[18px] sm:!h-[18px] lg:!w-[24px] lg:!h-[24px]">
                            <path d="M9 18L15 12L9 6" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </motion.button>
                </motion.div>

                {/* Globe Section - Responsive: Mobile 400px, Desktop 736px */}
                <motion.div
                    className="relative w-full mx-auto h-[280px] sm:h-[500px] lg:h-[736px] mt-8 mb-0"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 1, delay: 0.8, ease: "easeOut" }}
                    style={{ overflow: 'visible' }}
                >
                    {/* Title - Moved to Globe Section Level for proper mobile width */}
                    <motion.div
                        className="absolute text-center text-white w-full left-0 right-0 top-[10px] sm:top-[40px] lg:top-[20px] px-4 sm:px-0"
                        initial={{ opacity: 0, y: -100 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                            type: "spring",
                            stiffness: 100,
                            damping: 15,
                            delay: 1.2
                        }}
                        style={{
                            fontFamily: 'Inter',
                            fontStyle: 'normal',
                            fontWeight: 400,
                            zIndex: 20
                        }}
                    >
                        <div className="text-center w-full mx-auto max-w-[calc(100vw-48px)] sm:max-w-[874.4px]">
                            <p className="mb-0 text-[24px] sm:text-[48px] md:text-[48px] lg:text-[56px] leading-[1.2] text-center">
                                <span className="block sm:inline">Trusted by top talents</span>
                                {' '}
                                <span className="block sm:inline whitespace-pre">across                the globe</span>
                            </p>
                        </div>
                    </motion.div>

                    {/* Main Container */}
                    <div
                        className="absolute scale-[0.4] sm:scale-[0.8] lg:scale-100 left-1/2 top-[60%] sm:top-[55%] lg:top-[52%] -translate-x-1/2 -translate-y-1/2"
                        style={{
                            width: '100%',
                            height: '100%',
                            zIndex: 1
                        }}
                    >
                        {/* Earth Globe - Optimized with WebP support */}
                        <OptimizedEarthGlobe />

                        {/* Circular blur mask to blend globe edges */}
                        <div
                            className="absolute w-[300px] h-[300px] sm:w-[400px] sm:h-[400px] lg:w-[680px] lg:h-[680px]"
                            style={{
                                left: '50%',
                                top: '50%',
                                transform: 'translate(-50%, -50%)',
                                borderRadius: '50%',
                                background: 'radial-gradient(circle at center, transparent 40%, rgba(2, 35, 52, 0.3) 65%, rgba(2, 35, 52, 0.5) 82%, transparent 95%)',
                                filter: 'blur(60px)',
                                zIndex: 2,
                                pointerEvents: 'none'
                            }}
                        />

                        {/* Blur 7 - Responsive */}
                        <div
                            className="absolute w-[250px] h-[250px] sm:w-[300px] sm:h-[300px] lg:w-[490px] lg:h-[490px]"
                            style={{
                                left: '50%',
                                top: '50%',
                                transform: 'translate(-50%, -50%)',
                                mixBlendMode: 'screen',
                                opacity: 0.5,
                                zIndex: 4
                            }}
                        >
                            {/* Primary Circle - CSS gradient */}
                            <div
                                className="absolute w-full h-full"
                                style={{
                                    left: '0',
                                    top: '0',
                                    background: 'radial-gradient(circle, rgba(79, 151, 255, 0.4) 0%, transparent 70%)',
                                    filter: 'blur(46.5275px)',
                                }}
                            />

                            {/* Secondary Circle - CSS gradient */}
                            <div
                                className="absolute w-[180px] h-[180px] sm:w-[230px] sm:h-[230px] lg:w-[375px] lg:h-[375px]"
                                style={{
                                    left: '50%',
                                    top: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    background: 'radial-gradient(circle, rgba(123, 159, 255, 0.35) 0%, transparent 70%)',
                                    filter: 'blur(93.0552px)',
                                }}
                            />
                        </div>

                        {/* Featured Companies Container - Responsive for Mobile */}
                        <div
                            className="absolute w-full sm:max-w-[874.4px]"
                            style={{
                                height: '100%',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                top: '0',
                                zIndex: 10
                            }}
                        >
                            {/* Company Logos Container - Same for all devices */}
                            <div>
                            {/* Citi with Polygon 15 - Both From LEFT */}
                            <motion.div
                                className="absolute"
                                initial={{ opacity: 0, x: -200 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{
                                    type: "spring",
                                    stiffness: 100,
                                    damping: 15,
                                    delay: 1.25
                                }}
                                style={{
                                    width: '154.4px',
                                    height: '60px',
                                    left: 'calc(50% - 138.4px/2 - 224px)',
                                    top: 'calc(50% - 41.6px/2 - 120px)',
                                }}
                            >
                                <img
                                    src="/globe_section/polygon-15.svg"
                                    alt=""
                                    className="absolute"
                                    style={{
                                        width: '257.6px',
                                        height: 'auto',
                                        left: '50%',
                                        top: '50%',
                                        transform: 'translate(-50%, -50%)',
                                    }}
                                />
                                <img
                                    src="/globe_section/citi-logo.svg"
                                    alt="Citi"
                                    className="absolute"
                                    style={{
                                        width: '128.8px',
                                        height: 'auto',
                                        left: '50%',
                                        top: '50%',
                                        transform: 'translate(-50%, -50%)',
                                    }}
                                />
                            </motion.div>

                            {/* TPG - From LEFT */}
                            <motion.div
                                className="absolute"
                                initial={{ opacity: 0, x: -200 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{
                                    type: "spring",
                                    stiffness: 100,
                                    damping: 15,
                                    delay: 1.3
                                }}
                                style={{
                                    width: '160.8px',
                                    height: '139.2px',
                                    left: 'calc(50% - 160.8px/2 - 216px)',
                                    top: 'calc(50% - 139.2px/2 + 40px)',
                                }}
                            >
                                <img
                                    src="/globe_section/polygon-14.svg"
                                    alt=""
                                    className="absolute"
                                    style={{
                                        width: '257.6px',
                                        height: 'auto',
                                        left: '50%',
                                        top: '50%',
                                        transform: 'translate(-50%, -50%)',
                                    }}
                                />
                                <img
                                    src="/globe_section/tpg-logo.svg"
                                    alt="TPG"
                                    className="absolute"
                                    style={{
                                        width: '110.4px',
                                        height: 'auto',
                                        left: '50%',
                                        top: '50%',
                                        transform: 'translate(-50%, -50%)',
                                        filter: 'drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.25))',
                                    }}
                                />
                            </motion.div>

                            {/* Polygon 16 - Empty hexagon above Barclays - From TOP */}
                            <motion.div
                                className="absolute"
                                initial={{ opacity: 0, y: -100 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{
                                    type: "spring",
                                    stiffness: 100,
                                    damping: 15,
                                    delay: 1.25
                                }}
                                style={{
                                    width: '160.8px',
                                    height: '139.2px',
                                    left: 'calc(50% - 160.8px/2 - 80px)',
                                    top: 'calc(50% - 139.2px/2 - 184px)',
                                }}
                            >
                                <img
                                    src="/globe_section/polygon-16.svg"
                                    alt=""
                                    className="absolute"
                                    style={{
                                        width: '160.8px',
                                        height: 'auto',
                                        left: '50%',
                                        top: '50%',
                                        transform: 'translate(-50%, -50%)',
                                        borderRadius: '10px',
                                    }}
                                />
                            </motion.div>

                            {/* Barclays - From LEFT */}
                            <motion.div
                                className="absolute"
                                initial={{ opacity: 0, x: -200 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{
                                    type: "spring",
                                    stiffness: 100,
                                    damping: 15,
                                    delay: 1.35
                                }}
                                style={{
                                    width: '160.8px',
                                    height: '139.2px',
                                    left: 'calc(50% - 160.8px/2 - 80px)',
                                    top: 'calc(50% - 139.2px/2 - 32px)',
                                }}
                            >
                                <img
                                    src="/globe_section/barclays-logo.svg"
                                    alt="Barclays"
                                    className="absolute"
                                    style={{
                                        width: '257.6px',
                                        height: 'auto',
                                        left: '50%',
                                        top: '50%',
                                        transform: 'translate(-50%, -50%)',
                                    }}
                                />
                            </motion.div>

                            {/* KKR - From BOTTOM (with Polygon 19) */}
                            <motion.div
                                className="absolute"
                                initial={{ opacity: 0, y: 200 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{
                                    type: "spring",
                                    stiffness: 100,
                                    damping: 15,
                                    delay: 1.4
                                }}
                                style={{
                                    width: '160.8px',
                                    height: '139.2px',
                                    left: 'calc(50% - 160.8px/2 - 80px)',
                                    top: 'calc(50% - 139.2px/2 + 120px)',
                                }}
                            >
                                 <img
                                    src="/globe_section/polygon-19.svg"
                                    alt=""
                                    className="absolute"
                                    style={{
                                        width: '257.6px',
                                        height: 'auto',
                                        left: '50%',
                                        top: '50%',
                                        transform: 'translate(-50%, -50%)',
                                    }}
                                />
                                <img
                                    src="/globe_section/kkr-logo.svg"
                                    alt="KKR"
                                    className="absolute"
                                    style={{
                                        width: '110.4px',
                                        height: 'auto',
                                        left: '50%',
                                        top: '50%',
                                        transform: 'translate(-50%, -50%)',
                                    }}
                                />
                            </motion.div>

                            {/* Polygon 17 - Empty hexagon above Brookfield - From BOTTOM */}
                            <motion.div
                                className="absolute"
                                initial={{ opacity: 0, y: 200 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{
                                    type: "spring",
                                    stiffness: 100,
                                    damping: 15,
                                    delay: 1.3
                                }}
                                style={{
                                    width: '160.8px',
                                    height: '139.2px',
                                    left: 'calc(50% - 160.8px/2 + 60px)',
                                    top: 'calc(50% - 139.2px/2 - 256px)',
                                }}
                            >
                                <img
                                    src="/globe_section/polygon-17.svg"
                                    alt=""
                                    className="absolute"
                                    style={{
                                        width: '160.8px',
                                        height: 'auto',
                                        left: '50%',
                                        top: '50%',
                                        transform: 'translate(-50%, -50%)',
                                        borderRadius: '10px',
                                    }}
                                />
                            </motion.div>

                            {/* Brookfield - From BOTTOM */}
                            <motion.div
                                className="absolute"
                                initial={{ opacity: 0, y: 200 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{
                                    type: "spring",
                                    stiffness: 100,
                                    damping: 15,
                                    delay: 1.45
                                }}
                                style={{
                                    width: '160.8px',
                                    height: '139.2px',
                                    left: 'calc(50% - 160.8px/2 + 60px)',
                                    top: 'calc(50% - 139.2px/2 - 104px)',
                                }}
                            >
                                <img
                                    src="/globe_section/brookfield-logo.svg"
                                    alt="Brookfield"
                                    className="absolute"
                                    style={{
                                        width: '257.6px',
                                        height: 'auto',
                                        left: '50%',
                                        top: '50%',
                                        transform: 'translate(-50%, -50%)',
                                        mixBlendMode: 'plus-lighter',
                                    }}
                                />
                            </motion.div>

                            {/* CITADEL - From BOTTOM */}
                            <motion.div
                                className="absolute"
                                initial={{ opacity: 0, y: 200 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{
                                    type: "spring",
                                    stiffness: 100,
                                    damping: 15,
                                    delay: 1.5
                                }}
                                style={{
                                    width: '160.8px',
                                    height: '139.2px',
                                    left: 'calc(50% - 160.8px/2 + 60px)',
                                    top: 'calc(50% - 139.2px/2 + 48px)',
                                }}
                            >
                                <img
                                    src="/globe_section/citadel-logo.svg"
                                    alt="CITADEL"
                                    className="absolute"
                                    style={{
                                        width: '257.6px',
                                        height: 'auto',
                                        left: '50%',
                                        top: '50%',
                                        transform: 'translate(-50%, -50%)',
                                        mixBlendMode: 'color-dodge',
                                    }}
                                />
                            </motion.div>

                            {/* Polygon 18 - Empty hexagon below CITADEL - From BOTTOM */}
                            <motion.div
                                className="absolute"
                                initial={{ opacity: 0, y: 200 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{
                                    type: "spring",
                                    stiffness: 100,
                                    damping: 15,
                                    delay: 1.55
                                }}
                                style={{
                                    width: '160.8px',
                                    height: '139.2px',
                                    left: 'calc(50% - 160.8px/2 + 60px)',
                                    top: 'calc(50% - 139.2px/2 + 200px)',
                                }}
                            >
                                <img
                                    src="/globe_section/polygon-18.svg"
                                    alt=""
                                    className="absolute"
                                    style={{
                                        width: '160.8px',
                                        height: 'auto',
                                        left: '50%',
                                        top: '50%',
                                        transform: 'translate(-50%, -50%)',
                                        borderRadius: '10px',
                                    }}
                                />
                            </motion.div>

                            {/* PIMCO - From BOTTOM */}
                            <motion.div
                                className="absolute"
                                initial={{ opacity: 0, y: 200 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{
                                    type: "spring",
                                    stiffness: 100,
                                    damping: 15,
                                    delay: 1.6
                                }}
                                style={{
                                    width: '160.8px',
                                    height: '139.2px',
                                    left: 'calc(50% - 160.8px/2 + 192px)',
                                    top: 'calc(50% - 139.2px/2 - 32px)',
                                }}
                            >
                                <img
                                    src="/globe_section/pimco-logo.svg"
                                    alt="PIMCO"
                                    className="absolute"
                                    style={{
                                        width: '257.6px',
                                        height: 'auto',
                                        left: '50%',
                                        top: '50%',
                                        transform: 'translate(-50%, -50%)',
                                        mixBlendMode: 'plus-lighter',
                                    }}
                                />
                            </motion.div>

                            {/* Polygon 20 - Empty hexagon below PIMCO - From BOTTOM */}
                            <motion.div
                                className="absolute"
                                initial={{ opacity: 0, y: 200 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{
                                    type: "spring",
                                    stiffness: 100,
                                    damping: 15,
                                    delay: 1.65
                                }}
                                style={{
                                    width: '160.8px',
                                    height: '139.2px',
                                    left: 'calc(50% - 160.8px/2 + 192px)',
                                    top: 'calc(50% - 139.2px/2 + 128px)',
                                }}
                            >
                                <img
                                    src="/globe_section/polygon-19.svg"
                                    alt=""
                                    className="absolute"
                                    style={{
                                        width: '160.8px',
                                        height: 'auto',
                                        left: '50%',
                                        top: '50%',
                                        transform: 'translate(-50%, -50%)',
                                        borderRadius: '10px',
                                    }}
                                />
                            </motion.div>
                            </div> {/* End of Company Logos Container */}
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default Hero;
